import DashboardIcon from '@/components/icons/sidebar/DashboardIcon';
import MaintenanceIcon from '@/components/icons/sidebar/MaintanceIcon';
import MessagesIcon from '@/components/icons/sidebar/messages';
import SupportIcon from '@/components/icons/sidebar/ChatIcon';
import UsersIcon from '@/components/icons/sidebar/UsersIcon';
import UserEditIcon from '@/components/icons/sidebar/UserEditIcon';
import DocumentLinesIcon from '@/components/icons/sidebar/DocumentLinesIcon';
import type { ComponentType } from 'react';

/** Spatie permission names — user needs at least one to see the link. */
export type SidebarNavItem = {
    name: string;
    to: string;
    permissionsAny?: string[];
    planFeature?: string;
};

export type SidebarRootItem = {
    name: string;
    icon: ComponentType<{ className?: string }>;
    activeIcon: ComponentType<{ className?: string }>;
    to: string;
    permissionsAny?: string[];
    planFeature?: string;
    navigation?: SidebarNavItem[];
    ownerOnly?: boolean;
    /** When true, the root item only toggles its dropdown instead of navigating to `to`. */
    dropdownOnly?: boolean;
};

export const navigationBottom = [
    { name: 'messages', icon: MessagesIcon, activeIcon: MessagesIcon, to: '' },
    { name: 'settings', icon: MaintenanceIcon, activeIcon: MaintenanceIcon, to: '/settings' },
    { name: 'help_and_support', icon: SupportIcon, activeIcon: SupportIcon, to: '' },
];

export const humanResources: SidebarNavItem[] = [
    { name: 'employees', to: '/human-resources/employees', permissionsAny: ['list_employee'] },
    { name: 'attendance_and_departure', to: '/human-resources/departure', permissionsAny: ['list_attendance'] },
    {
        name: 'shifts_and_holidays',
        to: '/human-resources/shifts',
        permissionsAny: ['list_shift', 'list_holiday_request', 'dashboard_holiday_request'],
    },
    { name: 'contract_management', to: '/human-resources/contract-management', permissionsAny: ['list_contract'] },
    { name: 'departments', to: '/human-resources/departments', permissionsAny: ['list_department'] },
    // {
    //     name: 'department_managers',
    //     to: '/human-resources/department-managers',
    //     permissionsAny: ['list_department_manager'],
    // },
    { name: 'branches', to: '/human-resources/branches', permissionsAny: ['list_branch'] },
    // { name: 'branch_managers', to: '/human-resources/branch-managers', permissionsAny: ['list_branch_manager'] },
    { name: 'taskss', to: '/human-resources/tasks', permissionsAny: ['list_task'], planFeature: 'tasks' },
    // {
    //     name: 'employees_reviews',
    //     to: '/human-resources/employees-reviews',
    //     permissionsAny: ['list_review'],
    //     planFeature: 'reviews',
    // },
    {
        name: 'hr_self_services',
        to: '/human-resources/self-services',
        permissionsAny: ['list_letter_request', 'list_service_request', 'list_employee_complaint', 'list_loan_request'],
    },
    {
        name: 'loans',
        to: '/human-resources/personal-loans',
        permissionsAny: ['list_loan_request'],
        planFeature: 'loans',
    },
    { name: 'trainees', to: '/human-resources/trainees', permissionsAny: ['list_trainee'], planFeature: 'trainees' },
];

export const FinancesResources: SidebarNavItem[] = [
    {
        name: 'salaries',
        to: '/finances/salaries',
        permissionsAny: ['list_employee_salary', 'list_deduction'],
        planFeature: 'payroll',
    },
    { name: 'payroll_sheet_nav', to: '/finances/payroll', permissionsAny: ['list_payroll'], planFeature: 'payroll' },

    {
        name: 'السلف والقروض',
        to: '/finances/personal-loans',
        permissionsAny: ['list_loan_request'],
        planFeature: 'loans',
    },
    {
        name: 'الخصومات والبدلات',
        to: '/finances/deductions',
        permissionsAny: ['list_deduction', 'list_allowance', 'dashboard_deduction', 'dashboard_allowance'],
        planFeature: 'payroll',
    },
];

export const selfServices: SidebarNavItem[] = [
    { name: 'employee_profile', to: '/self-services', permissionsAny: ['access_self_service'] },
    { name: 'holidays_requests', to: '/self-services/holidays-requests', permissionsAny: ['access_self_service'] },
    { name: 'insurances', to: '/self-services/insurances', permissionsAny: ['access_self_service'] },
    { name: 'loans', to: '/self-services/loans', permissionsAny: ['access_self_service'] },
    { name: 'services', to: '/self-services/services', permissionsAny: ['access_self_service'] },
    { name: 'reports', to: '/self-services/reports', permissionsAny: ['access_self_service'] },
];

export const careerPath: SidebarNavItem[] = [
    { name: 'job', to: '/career-path/job', permissionsAny: ['list_job', 'dashboard_job'], planFeature: 'career_path' },
    {
        name: 'interviews',
        to: '/career-path/interviews',
        permissionsAny: ['list_interview', 'dashboard_interview'],
        planFeature: 'career_path',
    },
    {
        name: 'applicants',
        to: '/career-path/applicants',
        permissionsAny: ['list_applicant'],
        planFeature: 'career_path',
    },
];

export const subscriptionsNavigation: SidebarNavItem[] = [
    { name: 'subscription_plans', to: '/subscriptions/plans' },
    { name: 'subscription_tenants', to: '/subscriptions/tenants' },
    { name: 'subscription_requests', to: '/subscriptions/requests' },
    { name: 'subscription_platform_settings', to: '/subscriptions/platform-settings' },
];

export const accessControlNavigation: SidebarNavItem[] = [
    { name: 'access_control_roles', to: '/access-control/roles', permissionsAny: ['list_role'] },
    { name: 'access_control_permissions', to: '/access-control/permissions', permissionsAny: ['list_permission'] },
    {
        name: 'access_control_user_roles',
        to: '/access-control/user-roles',
        permissionsAny: ['assign_permission', 'list_user'],
    },
];

export const navigation: SidebarRootItem[] = [
    {
        name: 'dashboard',
        icon: DashboardIcon,
        activeIcon: DashboardIcon,
        to: '/',
        permissionsAny: ['list_dashboard', 'access_self_service'],
    },
    {
        name: 'human_resources',
        icon: UsersIcon,
        activeIcon: UsersIcon,
        to: '/human-resources',
        navigation: humanResources,
    },
    {
        name: 'finances',
        icon: UsersIcon,
        activeIcon: UsersIcon,
        to: '/finances',
        planFeature: 'payroll',
        navigation: FinancesResources,
        dropdownOnly: true,
    },
    {
        name: 'self_services',
        icon: UserEditIcon,
        activeIcon: UserEditIcon,
        to: '/self-services',
        navigation: selfServices,
    },
    {
        name: 'career_path',
        icon: UsersIcon,
        activeIcon: UsersIcon,
        to: '/career-path',
        planFeature: 'career_path',
        navigation: careerPath,
    },
    {
        name: 'access_control',
        icon: DocumentLinesIcon,
        activeIcon: DocumentLinesIcon,
        to: '/access-control',
        planFeature: 'access_control',
        navigation: accessControlNavigation,
    },
    {
        name: 'subscriptions',
        icon: DocumentLinesIcon,
        activeIcon: DocumentLinesIcon,
        to: '/subscriptions',
        ownerOnly: true,
        navigation: subscriptionsNavigation,
    },
];
